import { ColumnConfig, SortDirections, wafflesKeyColumnName, WafflesPaginationProps } from "@app/shared/waffles";
import { Waffles } from "@app/shared/waffles/Waffles";
import { JSX, useEffect, useMemo, useState } from "react";
import { QESAlert } from "@app/shared/qes-standards/Alert";
import { Version } from "@app/members/memberSetModels";
import MemberSetService from "@app/members/memberSetService";
import { OmniusCargoColumnType } from "@app/shared/griddle/CargoColumns";
import { mapOmniusCargoTypeToFilterType } from "@app/shared/waffles/filterExports";

interface MemberSetPreviewTableProps {
  memberSetVersion: Version;
  memberCount: number;
}

interface MemberSetPreviewTableData {
  [wafflesKeyColumnName]: string;
  countyName: string;
  countySSA: string;
  latitude: number;
  longitude: number;
  state: string;
  zip: string;
}

type MemberSetPreviewTableDataWithCargo = {
  [key: string]: string | number | boolean;
} & MemberSetPreviewTableData;

interface MemberPreviewSorting {
  sortDirection: SortDirections;
  sortedColumn: keyof MemberSetPreviewTableDataWithCargo;
}

const defaultMemberSetPreviewColumnConfig: ColumnConfig = {
  countyName: {
    displayText: "County",
    sortOrder: 20,
    testId: "countyName",
  },
  countySSA: {
    displayText: "County SSA",
    sortOrder: 30,
    testId: "countySSA",
  },
  latitude: {
    displayText: "Latitude",
    sortOrder: 3000, // arbitrarily large number to make plenty of space for cargo columns
    testId: "latitude",
  },
  longitude: {
    displayText: "Longitude",
    sortOrder: 3100,
    testId: "logitude,",
  },
  state: {
    displayText: "State",
    sortOrder: 10,
    testId: "state",
  },
  zip: {
    displayText: "Zip",
    sortOrder: 40,
    testId: "zip",
  },
};

const MemberSetPreviewTable = (props: MemberSetPreviewTableProps): JSX.Element => {

  const memberSetService = new MemberSetService();
  const totalRecords = props.memberCount;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<JSX.Element>(null);
  const [members, setMembers] = useState<MemberSetPreviewTableDataWithCargo[]>([]);
  const [sorting, setSorting] = useState<MemberPreviewSorting>({ sortDirection: SortDirections.None, sortedColumn: null });
  const [columnConfig, setColumnConfig] = useState<ColumnConfig>(defaultMemberSetPreviewColumnConfig);
  const [skip, setSkip] = useState(0);
  const [take, setTake] = useState(100);

  useEffect(() => {
    setLoading(true);
    //(skip / take) + 1 because of how waffles was set up to calculate page numbers, API just wants the page number (starting at 1)
    memberSetService.getMemberSetMembers(props.memberSetVersion.memberSetId, props.memberSetVersion.memberSetVersionId, take, (skip / take) + 1, sorting.sortDirection, sorting.sortedColumn as string).then((result) => {
      const cargoCols: ColumnConfig = {};
      if (result.length > 0 && result[0].cargoColumns.length > 0) {
        result[0].cargoColumns.forEach((cargo, index) => {
          Object.assign(cargoCols, {
            [cargo.name]: {
              displayText: cargo.name,
              filterType: mapOmniusCargoTypeToFilterType(cargo.type),
              sortOrder: 40 + index + 1,
              testId: cargo.name,
            },
          });
        });
      }
      const newColumnConfig: ColumnConfig = {
        ...columnConfig,
        ...cargoCols,
      };
      setColumnConfig(newColumnConfig);
      setMembers(result.map((member) => {
        const { cargoColumns, ...rest } = member;
        const mappedMembers: MemberSetPreviewTableDataWithCargo = {
          [wafflesKeyColumnName]: member.countySSA,
          ...rest,
        };
        member.cargoColumns.forEach((cargo) => {
          if (cargo.type === OmniusCargoColumnType.bool) {
            mappedMembers[cargo.name] = !!cargo.value ? "Yes" : "";
          } else {
            mappedMembers[cargo.name] = cargo.value;
          }
        });
        return mappedMembers;
      }));
      setLoading(false);
    }, () => {
      setError(<QESAlert type="danger">Something went wrong on our end.</QESAlert>);
    });
  }, [props.memberSetVersion, skip, take, sorting]);

  useEffect(() => {
    setSkip(0);
  }, [props.memberSetVersion.memberSetVersionId]);

  const customSort = (records: MemberSetPreviewTableDataWithCargo[], column: keyof MemberSetPreviewTableDataWithCargo, sort: SortDirections) => {
    setLoading(true);
    setSorting({ sortDirection: sort, sortedColumn: column });
    return records;
  };

  const handleChangePage = (pageNumber: number) => {
    setLoading(true);
    setSkip(Math.max((pageNumber - 1) * take, 0));
  };

  if (error) {
    return error;
  }

  const handleChangeRecordsPerPage = (pageNumber: number, recordsPerPage: number) => {
    if (recordsPerPage !== take) {
      setLoading(true);
      setTake(recordsPerPage);
      setSkip(pageNumber - 1 * recordsPerPage);
    }
  };

  const paginationProps: WafflesPaginationProps = useMemo(() => {
    return {
      changePage: handleChangePage,
      changeRecordsPerPage: handleChangeRecordsPerPage,
      recordsPerPage: take,
      skip,
      totalRecords,
    };
  }, [take, skip, totalRecords]);

  return (
    <>
      <Waffles<MemberSetPreviewTableDataWithCargo>
        columns={columnConfig}
        customSort={customSort}
        data={members}
        fullWidth={true}
        loading={loading}
        enablePagination={true}
        paginationProps={paginationProps}
        passThroughSorting={true}
        testId="member-preview"
      />
    </>
  );
};

export default MemberSetPreviewTable;
